/* eslint-disable array-callback-return */
import { clone, isEmpty, isNil } from "ramda";
import { Marker, Popup } from "react-leaflet";
import Button from "@mui/material/Button";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoIcon from "@mui/icons-material/Photo";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import MapIcon from "@mui/icons-material/Map";
import { ALERT_COLORS } from "../../../../../utils/consts";
import { getAlertThemeData, getMuiTheme, handlePointClick } from "../../../../../utils/utils";
import palette from "../../../../../../config/colorPalette";
import { ThemeProvider } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useMemo, useState } from "react";
import { Chip } from "@mui/material";
import { postReport } from "../../../../../api";
export const getCentroid = (points) => {
  if (isNil(points) || isEmpty(points)) {
    return [0, 0];
  }
  let latSum = 0,
    longSum = 0;
  points.forEach((point) => {
    latSum += point[0];
    longSum += point[1];
  });
  return [latSum / points.length, longSum / points.length];
};

export const getMeasure = (measures, mins, maxs, thresholds, domain) => {
  if (isNil(measures)) return 0;
  const list = Array.isArray(measures) ? measures : [measures];

  const th = [...thresholds];
  while (th.length < domain.length) {
    const last = th[th.length - 1];
    th.push(isNil(last) ? Infinity : last);
  }
  const hasThresholds = th.some((t) => Number.isFinite(t));

  let result = 0;
  for (let index = 0; index < list.length; index++) {
    const number = list[index];
    if (!Number.isFinite(number)) continue;

    if (hasThresholds) {
      for (let j = domain.length - 1; j >= 0; j--) {
        if (th[j] <= number) {
          if (result < domain[j]) result = domain[j];
          if (j === domain.length - 1) return result;
        }
      }
    }

    if (!hasThresholds || number < th[0]) {
      const range = maxs[index] - mins[index];
      let temp = range > 0 ? (number - mins[index]) / range : 0;
      temp = Math.min(Math.max(temp, 0), 1);
      if (result < temp) result = temp;
    }
  }
  return result;
};
export const getMeasureState = (measures, thresholds) => {
  let result = 0;
  for (let index = 0; index < measures.length; index++) {
    const number = measures[index];
    const th = clone(thresholds[index]);
    for (let thIndex = 0; thIndex < th.length; thIndex++) {
      if (number >= th[thIndex]) {
        result = 1;
      }
    }
  }
  return result;
};

export const getLineColor = ({ filter, p, max, min, tColorScale, umbrales }) => {
  const values = !isNil(p.values) ? p.values : p.measuresValue;
  const measure =
    filter === "status" || filter === "status.secondary"
      ? getMeasureState(values, umbrales)
      : getMeasure(values, [min], [max], umbrales, tColorScale.domain());
  return tColorScale(Number.isFinite(measure) ? measure : 0);
};

export const getMaxMin = (polylines) => {
  const measureTypesCount = polylines?.[0]?.p1?.values?.length;
  const maxValues = [];
  const minValues = [];

  for (let measureIndex = 0; measureIndex < measureTypesCount; measureIndex++) {
    let currentMax = 0;
    let currentMin = 0;
    for (let index = 0; index < polylines?.length; index++) {
      const value = polylines?.[index]?.p1?.values?.[measureIndex];
      currentMax = index === 0 ? value : currentMax < value ? value : currentMax;
      currentMin = index === 0 ? value : currentMin > value ? value : currentMin;
    }
    maxValues.push(currentMax);
    minValues.push(currentMin);
  }
  return { maxValues, minValues };
};

export const shouldAddAccordingToState = ({
  indexes,
  index,
  thresholds,
  values,
  prevValues,
  filteredPoints,
  measures,
}) => {
  let measureValue = []; //values tendria que venir [temperatura, strain, leak, landslide] (dependiendo del orden en el que llegue)
  for (let i = 0; i < measures.length; i++) {
    if (!isNil(values?.[measures[i]]?.[index])) {
      measureValue.push(values[measures[i]][index]);
    }
  }
  if (indexes.includes(index)) {
    prevValues.values = values;
    return { should: true, values };
  } else {
    //filteredPoints[filteredPoints.length - 1].measuresValue = clone(prevValues.values);
    prevValues = { values: values.map((v, i) => Math.max(v, prevValues.values[i])) };
  }
  return { should: false, measureValue: [] };
};

export const shouldAddAccordingToMeasure = ({
  indexes,
  index,
  thresholds,
  difference,
  value,
  prevValues,
  filteredPoints,
}) => {
  if (!isNil(value)) {
    if (indexes.includes(index) || Math.abs(value - prevValues?.values) > difference) {
      prevValues.values = [value];
      return { should: true, measureValue: [value] };
    } else {
      if (
        !isNil(filteredPoints[filteredPoints.length - 1]) &&
        !isNil(filteredPoints[filteredPoints.length - 1].measuresValue)
      )
        filteredPoints[filteredPoints.length - 1].measuresValue = Math.max(value, prevValues?.values?.[0]);
    }
  }
  return { should: false, measureValue: [] };
};

export const getAlertType = (values, thresholds) => {
  let alertType = 0; // 0 -> OK, 1 -> low, 2 -> intermediate, 3 -> high
  for (let i = 0; i < values.length && alertType < 3; i++) {
    const item = values[i];
    if (!isNil(thresholds[i])) {
      for (let j = thresholds[i].length - 1, type = 3; j >= 0 && alertType < 3; j--, type--) {
        const th = thresholds[i][j];
        if (th <= item && alertType < type) {
          alertType = type;
        }
      }
    }
  }
  return alertType;
};

/*const getMP = (p1, p2, offset_v1_latitude, offset_v1_longitude) => {
  var lat = 0;
  var lon = 0;
  var lat2 = 0;
  var lon2 = 0;
  var sumX = 0;
  var sumY = 0;
  var sumZ = 0;

  lat = (p1.latitude + offset_v1_latitude) * (Math.PI / 180);
  lat2 = (p2.latitude + offset_v1_latitude) * (Math.PI / 180);
  lon = (p1.longitude + offset_v1_longitude) * (Math.PI / 180);
  lon2 = (p2.longitude + offset_v1_longitude) * (Math.PI / 180);
  sumX = Math.cos(lat) * Math.cos(lon) + Math.cos(lat2) * Math.cos(lon2);
  sumY = Math.cos(lat) * Math.sin(lon) + Math.cos(lat2) * Math.sin(lon2);
  sumZ = Math.sin(lat) + Math.sin(lat2);

  var avgX = sumX / 2;
  var avgY = sumY / 2;
  var avgZ = sumZ / 2;

  lon = Math.atan2(avgY, avgX) * (180 / Math.PI);
  var hyp = Math.sqrt(avgX * avgX + avgY * avgY);
  lat = Math.atan2(avgZ, hyp) * (180 / Math.PI);

  return [lat, lon];
};*/
export function asociarNumeroConLista(numero, lista) {
  if (window.RUNTIME_CONFIG.rango.includes(numero)) {
    var indice = numero - window.RUNTIME_CONFIG.rango[0];
    return lista[indice];
  } else {
    return "Número fuera del rango especificado";
  }
}

export const computeAlerts = ({ inside, assetsValues, alerts }) => {
  const newAlerts = [];
  const newAlertsNames = [];

  if (!inside || !assetsValues?.data || !alerts?.measurements) {
    return { alerts: [], names: [] };
  }

  // 🔥 optimización: filtrar una sola vez
  const activeAlerts = alerts.measurements.filter((a) => !a.isArchived);

  for (let i = 0; i < inside.length; i++) {
    const asset = inside[i];

    if (!isNil(assetsValues.data[asset])) {
      const assetAlerts = activeAlerts.filter((a) => a.asset === asset);

      newAlerts.push(...assetAlerts);

      for (let j = 0; j < assetAlerts.length; j++) {
        const a = assetAlerts[j];
        newAlertsNames.push(`${a.module}-${a.package}`);
      }
    }
  }

  return {
    alerts: [...new Set(newAlerts)],
    names: [...new Set(newAlertsNames)],
  };
};
const iconCache = new Map();
const getCachedIcon = (factory, severity, magnitude, active) => {
  const key = `${active ? "gif" : "static"}_${severity}_${magnitude}`;
  if (!iconCache.has(key)) iconCache.set(key, factory(severity, magnitude));
  return iconCache.get(key);
};
export const printPolylineAlerts = ({
  configData,
  currentZoom,
  assetsAlerts,
  assetsAlertsFilter,
  selectedAssetsAlertsFilter,
  polylines,
  assets,
  selectedSubs,
  assetEquipments,
  selectedEquipment,
  inside,
  points,
  customIcon,
  createAlertIconGif,
  navigate,
  intl,
}) => {
  const markers = [];
  let offset_v1_latitude = 0;
  let offset_v1_longitude = 0;

  const asociado = asociarNumeroConLista(currentZoom, window.RUNTIME_CONFIG.lista);
  const subPolylines = {};
  const dobValues = ["partially.unburied", "unburied"];

  for (let indexAlert = 0; indexAlert < assetsAlerts.length; indexAlert++) {
    const actualAlert = assetsAlerts[indexAlert];
    const modulePackage = `${actualAlert.module}-${actualAlert.package}`;
    if (selectedAssetsAlertsFilter.includes(modulePackage)) {
      const assetData = assets.find((asset) => asset.name === actualAlert.asset);
      if (isNil(subPolylines[actualAlert.asset])) {
        subPolylines[actualAlert.asset] = [];
      }

      const equipment = assetData.equipments.find((equip) => equip.equipmentID === actualAlert.equipmentID);

      const assetsInPolylines = [];
      for (let index = 0; index < polylines.length; index++) {
        assetsInPolylines.push(polylines[index][polylines[index].length - 1].asset);
      }

      let count = 0;
      let pLineIndex = 0;
      for (let j = 0; j < assetsInPolylines.length; j++) {
        if (assetsInPolylines[j] === assetData.name) {
          count++;
          if (count === actualAlert.channel) {
            pLineIndex = j;
            break;
          }
        }
      }
      const assetIndex = inside.indexOf(assetData.name);

      const equipmentIndex = assetEquipments[assetIndex].findIndex((e) => e.equipmentID === equipment.equipmentID);

      const channel = equipment.channels[actualAlert.channel - 1];
      const channelSubs = channel.substructures;
      const subsFound = assetData.substructures.filter((sub) => channelSubs.includes(sub.ref));
      const actualSub = subsFound.find((sub) => sub.ref === actualAlert.substructure);
      const itsOn = selectedSubs[equipmentIndex].some((sub) => sub.ref === actualSub.ref);
      if (!isNil(actualSub) && itsOn) {
        subPolylines[actualAlert.asset].push(actualSub);
        const alreadyExists = subPolylines[actualAlert.asset].find(
          (sub) => sub.ref !== actualSub.ref && sub.polyline === actualSub.polyline,
        );
        const moduleData = configData.modules.find((module) => module.name === actualAlert.module);
        const packageData = moduleData.packages.find((packg) => packg.name === actualAlert.package);
        const polyline = polylines[pLineIndex];
        const repeated = polyline[polyline.length - 1].equal;
        const realIndex = pLineIndex - Math.min(...repeated);

        if (!isNil(alreadyExists) && alreadyExists.length > 1) {
          const half = Math.round(alreadyExists.length / 2);
          const m = realIndex + 1 > half ? 1 : -1;
          const extra =
            alreadyExists.length % 2 === 0 ? Math.round((realIndex + 1) / half) : Math.round(realIndex / half);
          const distance = asociado * m * extra;
          const pointIni = [polyline[0].p1.latitude, polyline[0].p1.longitude];
          const pointEnd = [polyline[polyline.length - 1].p2.latitude, polyline[polyline.length - 1].p2.longitude];
          const atanV1 = Math.atan2(pointEnd[0] - pointIni[0], pointEnd[1] - pointIni[1]);
          offset_v1_latitude = -distance * Math.cos(atanV1);
          offset_v1_longitude = distance * Math.sin(atanV1);
        }
        const actualPoints = points.data[`${assetData.name}_${actualSub.polyline}_${equipment.equipmentType}`];
        const aux = actualAlert.startIndex + parseInt((actualAlert.endIndex - actualAlert.startIndex) / 2);
        const middlepoint = [
          actualPoints.points[aux].latitude + offset_v1_latitude,
          actualPoints.points[aux].longitude + offset_v1_longitude,
        ];
        const alertData = getAlertThemeData(actualAlert.severity, "large", intl);
        const value = actualAlert.value >= 0 ? actualAlert.value : "-";
        const realMagnitude = actualAlert.package;
        const actualIcon = actualAlert.active
          ? getCachedIcon(createAlertIconGif, alertData.severity, realMagnitude, true)
          : getCachedIcon(customIcon, alertData.severity, realMagnitude, false);
        markers.push(
          <Marker
            key={`alert_${actualAlert.id}`}
            position={middlepoint}
            icon={actualIcon}
            pane="markersPane"
            severity={actualAlert.severity}
          >
            <Popup>
              <div>
                <div
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {alertData.icon}
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "0.1rem", gap: "0.5rem" }}>
                  <span style={{ fontWeight: "600" }}>{intl.formatMessage({ id: "date" })}:</span>
                  <span>
                    {actualAlert.dateStart
                      .replace("T", " ")
                      .replace(/(\+\d{2}:\d{2})/, "")
                      .replace(/\.\d+$/, "")}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem", gap: "0.5rem" }}>
                  <span style={{ fontWeight: "600" }}>PK:</span>
                  <span>{actualPoints.points[actualAlert.startIndex].pk.toFixed(1)}</span>
                </div>

                <table style={{ borderSpacing: "0", border: `1px solid ${palette.primary}` }}>
                  <thead>
                    <tr style={{ textAlign: "center", backgroundColor: palette.primary, color: palette.likeWhite }}>
                      <td
                        style={{
                          borderRight: `2px solid ${palette.likeWhite}`,
                        }}
                      >
                        {intl.formatMessage({ id: "type" })}
                      </td>
                      <td>{intl.formatMessage({ id: "value" })}</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      style={{
                        backgroundColor: ALERT_COLORS[alert.severity],
                        fontWeight: actualAlert.severity > 0 ? "600" : "400",
                      }}
                    >
                      <td
                        style={{
                          padding: "0.1rem 0.5rem",
                          borderRight: `2px solid ${palette.likeWhite}`,
                        }}
                      >
                        {intl.formatMessage({ id: realMagnitude })}
                      </td>
                      <td
                        style={{
                          padding: "0.1rem 0.5rem",
                        }}
                      >
                        {realMagnitude !== "DoB" ? value : intl.formatMessage({ id: dobValues[value - 1] })}{" "}
                        {packageData.unit}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div style={{ marginTop: "0.5rem", display: "flex", justifyContent: "center", cursor: "pointer" }}>
                  <Button
                    sx={{
                      fontSize: "0.7rem",
                      color: palette.primary,
                      borderColor: palette.primary,
                    }}
                    size="small"
                    variant="outlined"
                    startIcon={<QueryStatsIcon />}
                    key={`go_analysis_${actualAlert.id}`}
                    onClick={() => {
                      const date = actualAlert.dateStart.replace(" ", "T");
                      navigate(
                        `/analysis/${actualAlert.asset}/${actualAlert.module}/${
                          actualAlert.package
                        }/${encodeURIComponent(date)}/${actualAlert.startIndex}/${actualAlert.endIndex}/`,
                      );
                    }}
                  >
                    {intl.formatMessage({ id: "analysis" })}
                  </Button>
                  <Button
                    sx={{
                      fontSize: "0.7rem",
                      color: palette.primary,
                      borderColor: palette.primary,
                    }}
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon />}
                    key={`MAP_${actualAlert.id}`}
                    onClick={() => {
                      handlePointClick(middlepoint[0], middlepoint[1]);
                    }}
                  >
                    GOOGLE MAPS
                  </Button>
                  {/*<Button
                sx={{
                  fontSize: "0.7rem",
                  color: palette.primary,
                  borderColor: palette.primary,
                }}
                size="small"
                variant="outlined"
                startIcon={<TextSnippetIcon />}
                key={`informe_${alert.id}`}
                onClick={() => {
                  console.log(alert);
                  setOpen(true);
                }}
              >
                {intl.formatMessage({ id: "report" })}
              </Button> */}
                </div>
              </div>
            </Popup>
          </Marker>,
        );
      }
    }
  }
  /**<AlertReportModal alert={alert} open={open} setOpen={setOpen} intl={intl} key={alert.id + "report"} />*/
  return markers;
};
const IconDisplay = ({ file, fileIndex }) => {
  const parts = file.type.split("/");
  const type = parts[0];

  if (type === "image") {
    return <PhotoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "video") {
    return <OndemandVideoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "audio") {
    return <MusicNoteIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "compressed") {
    return <FolderZipIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (file.name?.endsWith(".pdf")) {
    return <PictureAsPdfIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  } else {
    return <InsertDriveFileIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
};
const handleClose = (setOpen, setFileList, setComment) => {
  setOpen(false);
  setFileList([]);
  setComment("");
};
const AlertReportModal = ({ alert, open, setOpen, intl }) => {
  const theme = useMemo(() => getMuiTheme(), []);
  const [comment, setComment] = useState("");
  const [fileList, setFileList] = useState([]);
  const realMagnitude = alert.package;

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={() => handleClose(setOpen, setFileList, setComment)}>
        <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
          {intl.formatMessage({ id: "report.alert" })}
        </DialogTitle>
        <DialogContent style={{ paddingBottom: "0" }}>
          <div style={{ paddingBottom: "5%" }}>
            <h3>{intl.formatMessage({ id: "alert.info" })}</h3>
            <span>
              <b>{intl.formatMessage({ id: "channel" })}:</b> {alert.channel}
            </span>
            <br />
            <span>
              <b>{intl.formatMessage({ id: "measurement" })}:</b> {intl.formatMessage({ id: realMagnitude })}
            </span>
            <br />
            <span>
              <b>{intl.formatMessage({ id: "date.start" })}:</b> {alert.dateStart}
            </span>
            <br />
            <span>
              <b>{intl.formatMessage({ id: "date.end" })}:</b> {alert.dateEnd}
            </span>
            <br />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
            {/* Columna izquierda: TextField + Upload Button */}
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <TextField
                multiline
                fullWidth
                label={intl.formatMessage({ id: "comment" })}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
                value={comment}
              />
              <Button
                variant="contained"
                color="primary"
                component="label"
                style={{ marginTop: "8px", alignSelf: "flex-start" }}
              >
                {intl.formatMessage({ id: "upload.file" })}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => {
                    const files = e.target.files;
                    setFileList((newFiles) => [...newFiles, ...files]);
                  }}
                />
              </Button>
            </div>

            {/* Columna derecha: Chips */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
              {fileList &&
                fileList.map((file, fileIndex) => (
                  <Chip
                    key={`${file.name}_${fileIndex}`}
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {file.name}
                        <IconDisplay file={file} fileIndex={fileIndex} />
                      </span>
                    }
                    onDelete={() => {
                      let newFiles = clone(fileList);
                      newFiles.splice(fileIndex, 1);
                      setFileList(newFiles);
                    }}
                  />
                ))}
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            style={{ backgroundColor: palette.secondary, color: "white" }}
            onClick={() => handleClose(setOpen, setFileList, setComment)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>

          <Button
            variant="contained"
            style={{ backgroundColor: palette.primary, color: "white" }}
            onClick={async () => {
              console.log(comment);
              console.log(fileList);
              const commenResult = await postReport({ comment: comment }, intl);
              console.log(commenResult);
              setComment("");
              setFileList([]);
            }}
          >
            {intl.formatMessage({ id: "accept" })}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export const alarmsQuantity = (alarms) => {
  let alerts = [];
  for (let i = 1; i < ALERT_COLORS.length; i++) {
    alerts.push(0);
  }
  if (!isNil(alarms) && !isEmpty(alarms)) {
    for (let i = 0; i < alarms.length; i++) {
      alerts[alarms[i].severity - 1]++;
    }
  }
  return alerts;
};

export const printEquipments = (keyPrefix, equipments, customEquipmentIcon, customEquipmentAlertIcon, alerts, intl) => {
  const markerEquipments = [];
  equipments.map((equip, equipIndex) => {
    const alertsEquipment = alerts.equipments.filter(
      (alert) => alert.equipmentID === equip.equipmentID && !alert.isArchived,
    );
    const markerIcon =
      equip.statusInfo.details === "OK" && equip.statusInfo.alive && isEmpty(alertsEquipment)
        ? customEquipmentIcon
        : customEquipmentAlertIcon;
    const position = [equip.latitude, equip.longitude];
    markerEquipments.push(
      <Marker key={`${keyPrefix}-${equipIndex}`} position={position} icon={markerIcon}>
        <Popup>
          <b>{intl.formatMessage({ id: "status.equipment" })}:</b>{" "}
          {intl.formatMessage({ id: equip.statusInfo.details })}
          <br />
          <b>Alive:</b>
          <span
            style={{
              backgroundColor: equip.statusInfo.alive ? "green" : "red",
              width: "10px",
              height: "10px",
              display: "inline-block",
              marginLeft: "5px",
            }}
          ></span>
          <br />
          <b>{intl.formatMessage({ id: "last.connection" })}:</b> {equip.statusInfo?.lastConnection?.replace("T", " ")}
        </Popup>
      </Marker>,
    );
  });
  return markerEquipments;
};

export const getBoundaries = (bIndex, assets) => {
  if (!isNil(assets)) {
    const result = [];
    for (let i = 0; i < assets.length; i++) {
      const asset = assets[i];
      result.push([]);
      for (let j = 0; j < asset.pBoundaries.length; j++) {
        const bound = asset.pBoundaries[j];
        result[i].push(bound[bIndex]);
      }
    }
    return result;
  }
  return null;
};

export function selectEquipment(
  value,
  assetEquipments,
  setFiltered_Asset,
  setMeasureMinus,
  setStateMagnitude,
  setSelectedEquipment,
  setIndexEquipmentChannel,
  setSelectedChannels,
  setFilter,
  setCurrentAsset,
  autocompleteRef,
) {
  let cont = 0;
  let selchannels = [];
  for (let index = 0; index < assetEquipments.length; index++) {
    for (let indexEquipment = 0; indexEquipment < assetEquipments[index].length; indexEquipment++) {
      selchannels.push([]);
      if (assetEquipments[index][indexEquipment].equipmentID === value) {
        setCurrentAsset(index);
        setFiltered_Asset(assetEquipments[index][indexEquipment].asset);
        setSelectedEquipment(value);
        setIndexEquipmentChannel(cont);
      }
      cont++;
    }
  }
  //setSelectedChannels(selchannels);
  //const ele = autocompleteRef.current.getElementsByClassName("MuiAutocomplete-clearIndicator")[0];
  //if (ele) ele.click();
}

export function selectEquipmentInAssetReceiving(
  value,
  assetEquipments,
  setFiltered_Asset,
  setMeasureMinus,
  setStateMagnitude,
  setSelectedEquipment,
  setIndexEquipmentChannel,
  setCurrentAsset,
  autocompleteRef,
) {
  let cont = 0;
  let newEquipment = null;
  let newEquipmentIndex = null;
  for (let index = 0; index < assetEquipments.length; index++) {
    for (let indexEquipment = 0; indexEquipment < assetEquipments[index].length; indexEquipment++) {
      if (assetEquipments[index][indexEquipment].equipmentID === value) {
        setCurrentAsset(index);
        setFiltered_Asset(assetEquipments[index][indexEquipment].asset);
        const measuresForStatus = [];
        //if (assetEquipments[index][indexEquipment].pMeasureAlerts.includes(1)) {
        //  const count = assetEquipments[index][indexEquipment].pMeasureAlerts.filter((alert) => alert === 1).length;
        //  const notStatus =
        //    assetEquipments[index][indexEquipment].pMeasureNames.includes("depth_of_burial") && count === 1;
        //  if (!notStatus && count > 1) {
        //    setMeasureMinus(1);
        //    for (
        //      let indexStatus = 0;
        //      indexStatus < assetEquipments[index][indexEquipment].pMeasureAlerts.length;
        //      indexStatus++
        //    ) {
        //      if (
        //        assetEquipments[index][indexEquipment].pMeasureAlerts[indexStatus] === 1 &&
        //        assetEquipments[index][indexEquipment].pMeasureNames[indexStatus] !== "depth_of_burial"
        //      ) {
        //        measuresForStatus.push(assetEquipments[index][indexEquipment].pMeasureNames[indexStatus]);
        //      }
        //    }
        //    setStateMagnitude(measuresForStatus);
        //  } else {
        //    setMeasureMinus(0);
        //  }
        //} else {
        //  setMeasureMinus(0);
        //  setStateMagnitude([]);
        //}
        newEquipment = value;
        newEquipmentIndex = cont;
      }
      cont++;
    }
  }
  if (isNil(newEquipment) && !isNil(assetEquipments) && !isEmpty(assetEquipments)) {
    newEquipment = assetEquipments[0][0].equipmentID;
  }
  if (isNil(newEquipmentIndex)) {
    newEquipmentIndex = 0;
  }
  setSelectedEquipment(newEquipment);
  setIndexEquipmentChannel(newEquipmentIndex);
}
